import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { HorizontalScrollSection, ScrollCard } from '@/components/HorizontalScrollSection';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin } from 'lucide-react';
import { optimizeImageUrl } from '@/lib/imageOptimize';

interface Location {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
}

interface LocationCarouselProps {
  title?: string;
  className?: string;
}

export const LocationCarousel = ({ title, className }: LocationCarouselProps) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    const { data, error } = await supabase
      .from('locations')
      .select('*')
      .eq('is_active', true)
      .eq('page_enabled', true)
      .order('display_order');

    if (!error && data) {
      setLocations(data as Location[]);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className={className}>
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="flex gap-4 overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-[45%] sm:w-[31%] lg:w-[24%]">
              <Skeleton className="aspect-[4/3] rounded-xl mb-3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (locations.length === 0) {
    return null;
  }

  return (
    <HorizontalScrollSection
      title={title || 'Browse by Location'}
      titleIcon={<MapPin className="h-5 w-5 text-muted-foreground" />}
      className={className}
    >
      {locations.map((location) => (
        <ScrollCard key={location.id}>
          <Link to={`/location/${location.slug}`} className="block group">
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-3">
              <img
                src={optimizeImageUrl(location.image_url, 'card') || 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&h=300&fit=crop&q=75'}
                alt={location.name}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-white font-bold text-lg md:text-xl">{location.name}</h3>
              </div>
            </div>
          </Link>
        </ScrollCard>
      ))}
    </HorizontalScrollSection>
  );
};
